import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Done from '@material-ui/icons/Done';
import DonutLarge from '@material-ui/icons/DonutLarge';
import MeetingRoomTwoTone from '@material-ui/icons/MeetingRoomTwoTone';
import PlayCircleOutlineOutlined from '@material-ui/icons/PlayCircleOutlineOutlined';
import RoomTwoTone from '@material-ui/icons/RoomTwoTone';
import Alert from '@material-ui/lab/Alert';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import Rating from '@material-ui/lab/Rating';
import { FORM_ERROR } from 'final-form';
import firebase from 'firebase';
import { filter, size } from 'lodash';
import { TextField } from 'mui-rff';
import React, { FC, useState } from 'react';
import ReactAvatarEdit from 'react-avatar-edit';
import { useCookies } from 'react-cookie';
import { Form } from 'react-final-form';
import { useCollection, useDocument } from 'react-firebase-hooks/firestore';
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import { WhatsappIcon, WhatsappShareButton } from 'react-share';
import { GameStatus } from './Game';
import { RoundStatus, useGameContext, useGames, usePlayer, usePlayers, useRoom } from './hooks';
import Screen from './Screen';

enum Dictionary {
  German,
  English,
  French,
  Latin,
  GreekGodsAndHeros,
  OldGermanWords,
  Empty,
}

const EnterRoomForm = ({ roomId, owner }: { roomId?: string, owner?: boolean }) => {
  const history = useHistory();
  const [, setCookie] = useCookies(['nua-oans']);
  const [avatar, setAvatar] = useState<string | undefined>(undefined);
  const onClose = () => setAvatar(undefined);
  return (
    <Form
      onSubmit={async (values: { room?: string, nickName?: string }) => {
        try {
          const roomRef = await firebase.firestore().doc(`Room/${values.room}`);
          const room = await roomRef.get();
          if (!room.exists) {
            return { room: `Raum ${values.room} existiert nicht` };
          }
          const player = await roomRef.collection('Player').add({ name: values.nickName, owner: Boolean(owner), active: true });
          if (avatar) {
            await player.update({ avatar });
          }
          if (owner) {
            roomRef.update({ createdBy: player });
          }
          setCookie('nua-oans', { roomId: values.room, playerId: player.id }, { path: '/', expires: new Date('9999-12-31') });
          history.push(`/room`);
        } catch (ex) {
          return { [FORM_ERROR]: JSON.stringify(ex) };
        }
      }}
      initialValues={{ room: roomId }}
      validate={({ room, nickName }) => {
        const errors = {} as { room?: string, nickName?: string };
        if (!room) {
          errors.room = 'Es muss ein Raum eingegeben werden';
        }
        if (!nickName) {
          errors.nickName = 'Es muss ein Spitzname eingegeben werden';
        }
        return errors;
      }}
      render={({ handleSubmit, submitError, invalid, pristine, submitting }) => (
        <>
          <CardContent>
            {!roomId &&
              <TextField name="room" label="Raum" variant="filled" required/>
            }
            <TextField name="nickName" label="Mein Spitzname" variant="filled" required/>
          </CardContent>
          <CardContent>
            <Typography>
              Bitte das Handy <strong>waagerecht</strong> halten (landscape), wenn ein Foto für den Avatar aufgenommen wird.
            </Typography>
            <Grid container>
              <Grid item xs={9}>
                <Typography variant="caption">Mein Avatar</Typography>
                <ReactAvatarEdit
                  label="Bitte wähle ein Bild"
                  width={200}
                  height={200}
                  onCrop={setAvatar}
                  onClose={onClose}
                  src={avatar}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption">Preview</Typography>
                <Avatar src={avatar}/>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button disabled={invalid || pristine || submitting} variant="contained" startIcon={<MeetingRoomTwoTone/>} onClick={handleSubmit}>Raum Betreten</Button>
            {submitError && <Alert severity="error">Fehler: {submitError}</Alert>}
          </CardActions>
        </>
      )}
    />
  );
};

export const CreateRoom = () => {
  const [roomId, setRoomId] = useState<string | undefined>(undefined);
  const [roomName, setRoomName] = useState<string | undefined>(undefined);
  
  if (roomId) {
    const url = new URL(`/room/enter/${roomId}`, document.baseURI).href;
    return (
      <Screen title="CreateRoom" hideBreadcrumbs>
        <Card>
          <CardHeader
            title="Du hast einen neuen Raum erzeugt"
            subheader="Lade nun deine Mitspieler ein."
          />
          <CardContent>
            <Typography>
              Gratulation! Du hast den neuen Raum mit den Namen <strong>{roomName}</strong> erzeugt.
              In diesem Raum kannst du mit deinen Mitspielern <strong>Nua Oans</strong> spielen.
            </Typography>
          </CardContent>
          <CardContent>
            <Typography>
              Du kannst jetzt die Einladungs-URL
              <blockquote>{url}</blockquote>
              dieses Raumes an deine Mitspieler verteilen.
            </Typography>
          </CardContent>
          <CardContent>
            <List>
              <ListItem button>
                <WhatsappShareButton url={url} title={`Einladung zu ${roomName}`}>
                  <Button><WhatsappIcon size={32} round/></Button>
                </WhatsappShareButton>
                <ListItemText>
                  Verschicke die Einladung über Whatsapp
                </ListItemText>
              </ListItem>
            </List>
          </CardContent>
          <EnterRoomForm owner roomId={roomId}/>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen title="CreateRoom" hideBreadcrumbs>
      <Card>
        <CardHeader
          title="Erzeuge einen neuen Raum"
        />
        <Form
          onSubmit={async (values: { name?: string, dictionary: Dictionary, terms?: string }) => {
            const room = await firebase.firestore().collection('Room').add({
              name: values.name,
            });
            setRoomId(room.id);
            setRoomName(values.name);
          }}
          initialValues={{ dictionary: Dictionary.German }}
          render={({ handleSubmit, invalid, pristine, submitting }) => (
            <>
              <CardContent>
                <TextField
                  name="name"
                  label="Beschreibung"
                  helperText="Name des Raumes"
                  variant="filled"
                  required
                />
                <TextField
                  name="dictionary"
                  label="Wörterbuch"
                  select
                  helperText="Welche Wörter sollen geraten werden?"
                  variant="filled"
                >
                  <MenuItem value={Dictionary.German}>Deutsch (Spielkarten von Just One)</MenuItem>
                  <MenuItem value={Dictionary.English}>Englisch</MenuItem>
                  <MenuItem value={Dictionary.French}>Französisch</MenuItem>
                  <MenuItem value={Dictionary.Latin}>Latein</MenuItem>
                  <MenuItem value={Dictionary.OldGermanWords}>altertümliche deutsche Wörter</MenuItem>
                  <MenuItem value={Dictionary.GreekGodsAndHeros}>Griechische Götter und Helden</MenuItem>
                  <MenuItem value={Dictionary.Empty}>leer (selber mit Wörtern zu befüllen)</MenuItem>
                </TextField>
                <TextField
                  name="terms"
                  label="Wörter"
                  placeholder="Wort1 Wort2 Wort3 ..."
                  helperText="Wörter durch Leerzeichen getrennt oder ein Wort pro Zeile"
                  multiline 
                  variant="filled"
                  rows={7}
                />
              </CardContent>
              <CardActions>
                <Button
                  disabled={invalid || pristine || submitting}
                  variant="contained"
                  startIcon={<RoomTwoTone/>}
                  onClick={handleSubmit}
                >
                  Raum erzeugen
                </Button>
              </CardActions>
            </>
          )}
        />
      </Card>
    </Screen>
  );
};

export const EnterRoom = () => {
  const { roomId } = useParams();
  const { roomId: roomIdFromCookies } = useGameContext();
  console.log({ roomId, roomIdFromCookies });
  const [room, loading, error] = useDocument(
    firebase.firestore().doc(`Room/${roomId}`)
  );
  if (roomId === roomIdFromCookies) {
    return <Room/>;
  }
  return (
    <Screen title="EnterRoom" hideBreadcrumbs>
      <Card>
        <CardHeader
          title={
            room?.exists ?
            `Betrete den Raum '${room.get('name')}'` :
            'Betrete einen Raum'
          }
        />
        <EnterRoomForm roomId={roomId}/>
     </Card>
    </Screen>
  );
};

type PlayerAvatarProps = {
  doc: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>,
};

const PlayerAvatar: FC<PlayerAvatarProps> = ({ doc }) => {
  const { name, avatar, active } = doc.data();
  return (
    <Badge
      overlap="circle"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      badgeContent="R"
      color="primary"
    >
      <Avatar src={avatar}>{name}</Avatar>
    </Badge>
  );
};

type GameListItemProps = {
  game: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>,
};

const GameListItem: FC<GameListItemProps> = ({ game }) => {
  const { roomId } = useGameContext();
  const [rounds, loading, error] = useCollection(
    firebase.firestore().collection(`Room/${roomId}/Game/${game.id}/Round`)
  );

  const correctRounds = size(filter(rounds?.docs, doc => doc.get('status') === RoundStatus.CORRECT));

  const { status } = game.data();
  return (
    <RouterLink to={`/room/game/${game.id}`}>
      <ListItem button>
        <ListItemIcon>
          {status === GameStatus.RUNNING ? <DonutLarge/> : <Done/>}
        </ListItemIcon>
        <ListItemText primary={`Spiel ${game.id}`}/>
        {status === GameStatus.ENDED &&
        <Rating max={13} value={correctRounds} disabled/>
        }
      </ListItem>
    </RouterLink>
  );
};

export const Room = () => {
  const { roomId } = useGameContext();
  const history = useHistory();

  const { room } = useRoom();
  const { games } = useGames();
  const { activePlayers } = usePlayers();
  const { player, isRoomOwner } = usePlayer();
  if (!room || !games || !player) {
    return <CircularProgress />;
  }
  const url = new URL(`/room/enter/${roomId}`, document.baseURI).href;
  return (
    <Screen title="Room">
      <Card>
        <CardHeader
          title={`Raum '${room.data()?.name}'`}
          subheader="In diesem Raum können mehrere Spiele gespielt werden"
        />
        <CardContent>
        <Typography variant="h6">
            Einladungs-URL
          </Typography>
          <Typography>
            {url}
          </Typography>
          <WhatsappShareButton url={url} title={`Einladung zu ${room.data()?.name}`}>
            <Button><WhatsappIcon size={32} round/></Button>
          </WhatsappShareButton>
        </CardContent>
        <CardContent>
          <Typography variant="h6">
            Mitspieler
          </Typography>
          <AvatarGroup style={{ marginLeft: 10 }}>
            {activePlayers.map((doc) => <PlayerAvatar key={doc.id} doc={doc}/>)}
          </AvatarGroup>
        </CardContent>
        <CardContent>
          <Typography variant="h6">
            Spiele
          </Typography>
          <List>
            {games.docs.map((doc) => <GameListItem game={doc}/>)}
          </List>
        </CardContent>
        <CardActions>
          {isRoomOwner &&
          <Button
            variant="contained"
            startIcon={<PlayCircleOutlineOutlined/>}
            onClick={async () => {
              const index = (games.docs.length || 0) + 1;
              await firebase.firestore().doc(`Room/${roomId}/Game/${index}`).set({
                index,
                status: GameStatus.RUNNING,
              });
              history.push(`/room/game/${index}`);
           }}
          >
            Neues Spiel starten
          </Button>
          }
        </CardActions>
      </Card>
    </Screen>
  );
};
